import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';
import Select from 'react-select';

const GenericImporterColumnMappingStep = ({
  config,
  headers,
  previewData,
  columnMappings,
  onColumnMappingChange,
  loading,
  onValidationChange
}) => {
  const [mappingErrors, setMappingErrors] = useState({});
  const [autoMapped, setAutoMapped] = useState(false);
  const [isValid, setIsValid] = useState(false);

  console.log('Column Mappings:', columnMappings);
  console.log('Preview Data:', previewData);

  // Auto-map columns on first render
  useEffect(() => {
    if (headers && headers.length > 0 && !autoMapped && Object.keys(columnMappings).length === 0) {
      const initialMappings = {};
      const fieldsConfig = config.fieldsConfig;

      // Try to map columns by name similarity
      Object.keys(fieldsConfig).forEach(fieldKey => {
        const fieldConfig = fieldsConfig[fieldKey];

        // Look for exact match first
        const exactMatch = headers.find(header =>
          header.toLowerCase() === fieldKey.toLowerCase() ||
          header.toLowerCase() === fieldConfig.label.toLowerCase()
        );

        if (exactMatch) {
          initialMappings[fieldKey] = exactMatch;
          return;
        }

        // Look for partial match
        const partialMatch = headers.find(header =>
          header.toLowerCase().includes(fieldKey.toLowerCase()) ||
          header.toLowerCase().includes(fieldConfig.label.toLowerCase())
        );

        if (partialMatch) {
          initialMappings[fieldKey] = partialMatch;
        }
      });

      // Set auto-mappings
      Object.keys(initialMappings).forEach(fieldKey => {
        onColumnMappingChange(fieldKey, initialMappings[fieldKey]);
      });

      setAutoMapped(true);
    }
  }, [headers, columnMappings, config.fieldsConfig, autoMapped, onColumnMappingChange]);

  // Validate current mappings
  useEffect(() => {
    const errors = {};
    let hasRequiredFieldsError = false;

    // Check if required fields are mapped
    config.requiredFields?.forEach(fieldKey => {
      if (!columnMappings[fieldKey]) {
        errors[fieldKey] = `Fältet "${config.fieldsConfig[fieldKey].label}" är obligatoriskt`;
        hasRequiredFieldsError = true;
      }
    });

    setMappingErrors(errors);

    // Update validation state
    const newIsValid = !hasRequiredFieldsError;
    setIsValid(newIsValid);

    // Communicate validation state to parent component
    if (onValidationChange) {
      onValidationChange(newIsValid);
    }
  }, [columnMappings, config.requiredFields, config.fieldsConfig, onValidationChange]);

  // Handle column selection
  const handleSelect = (fieldKey, selected) => {
    if (selected) {
      onColumnMappingChange(fieldKey, selected.value);
    } else {
      // Handle clearing the selection
      onColumnMappingChange(fieldKey, null);
    }
  };

  return (
    <>
      <style>{`
        .mapping-container {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        
        .mapping-instructions {
          background: #f9fafb;
          padding: 1rem;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          margin-bottom: 1.5rem;
        }
        
        .mapping-instruction-title {
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #1e293b;
        }
        
        .mapping-instruction-text {
          color: #64748b;
          font-size: 0.875rem;
          line-height: 1.5;
        }
        
        .mapping-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .mapping-table th {
          text-align: left;
          padding: 0.75rem 1rem;
          background: #f9fafb;
          font-weight: 600;
          color: #1e293b;
          border-bottom: 2px solid #e5e7eb;
        }
        
        .mapping-table td {
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .mapping-field-name {
          font-weight: 600;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .required-indicator {
          color: #dc2626;
          font-weight: 600;
          margin-left: 2px;
        }
        
        .field-description {
          color: #64748b;
          font-size: 0.875rem;
          margin-top: 0.25rem;
        }
        
        .alert-warning {
          background-color: #fef2f2;
          border: 1px solid #fee2e2;
          border-radius: 6px;
          padding: 1rem;
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }
        
        .alert-warning-icon {
          color: #dc2626;
          flex-shrink: 0;
        }
        
        .alert-warning-content {
          flex: 1;
        }
        
        .alert-warning-title {
          color: #b91c1c;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }
        
        .alert-warning-text {
          color: #dc2626;
          font-size: 0.875rem;
        }

        .react-select-container {
          width: 100%;
        }
        
        .react-select__control {
          border-color: #d1d5db !important;
          box-shadow: none !important;
          border-radius: 4px !important;
          min-height: 38px !important;
        }
        
        .react-select__control--is-focused {
          border-color: #44814E !important;
          box-shadow: 0 0 0 1px #44814E !important;
        }
        
        .react-select__menu {
          border-radius: 4px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
        }
        
        .react-select__option--is-selected {
          background-color: #44814E !important;
        }
        
        .example-value {
          font-size: 0.875rem;
          color: #475569;
          padding: 0.5rem;
          
          font-family: monospace;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .example-empty {
          color: #94a3b8;
          font-style: italic;
        }
        
        .validation-error {
          color: #dc2626;
          font-size: 0.75rem;
          margin-top: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        /* Subtle border indication for required fields with error */
        .error-field .react-select__control {
          border-color: #dc2626 !important;
          background-color: #fef2f2 !important;
        }
        
        .validation-status {
          display: flex;
          align-items: center;
          margin-top: 1.5rem;
          padding: 0.75rem;
          border-radius: 4px;
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
        }
        
        .validation-status-error {
          color: #b91c1c;
        }
        
        .validation-status-success {
          color: #047857;
        }
        
        .validation-status-icon {
          margin-right: 0.75rem;
          flex-shrink: 0;
        }

        .unmapped-fields-list {
          margin-top: 0.5rem;
          padding-left: 1.5rem;
          font-size: 0.875rem;
        }

        .unmapped-field-item {
          margin-bottom: 0.25rem;
        }
        
        @media (max-width: 768px) {
          .mapping-table thead th:nth-child(3) {
            display: none;
          }
          
          .mapping-table tbody td:nth-child(3) {
            display: none;
          }
        }
      `}</style>

      <div className="mapping-container">
        <div className="mapping-instructions">
          <div className="mapping-instruction-title">
            <HelpCircle size={16} />
            Instruktioner för kolumnmappning
          </div>
          <div className="mapping-instruction-text">
            Koppla kolumnerna i din CSV-fil till motsvarande fält i systemet.
            Obligatoriska fält är markerade med en asterisk (*).
            Systemet har försökt matcha kolumnerna automatiskt baserat på deras namn.
          </div>
        </div>

        {Object.keys(mappingErrors).length > 0 && (
          <div className="alert-warning">
            <AlertTriangle className="alert-warning-icon" size={20} />
            <div className="alert-warning-content">
              <div className="alert-warning-title">
                Obligatoriska fält saknas
              </div>
              <div className="alert-warning-text">
                Vänligen mappa alla obligatoriska fält för att fortsätta.
              </div>
              <ul className="unmapped-fields-list">
                {Object.entries(mappingErrors).map(([fieldKey, errorMessage]) => (
                  <li key={fieldKey} className="unmapped-field-item">
                    {config.fieldsConfig[fieldKey].label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <table className="mapping-table">
          <thead>
            <tr>
              <th style={{ width: '30%' }}>Systemfält</th>
              <th style={{ width: '40%' }}>CSV-kolumn</th>
              <th style={{ width: '30%' }}>Exempelvärde</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(config.fieldsConfig).map((fieldKey) => {
              const fieldConfig = config.fieldsConfig[fieldKey];
              const isRequired = config.requiredFields?.includes(fieldKey);
              const hasError = mappingErrors[fieldKey];
              const selectedValue = columnMappings[fieldKey];
              const exampleValue = selectedValue && previewData && previewData[0] ?
                previewData[0][selectedValue] : null;

              return (
                <tr
                  key={fieldKey}
                >
                  <td>
                    <div className="mapping-field-name">
                      {fieldConfig.label}
                      {isRequired && <span className="required-indicator">*</span>}
                    </div>
                    {fieldConfig.description && (
                      <div className="field-description">{fieldConfig.description}</div>
                    )}
                  </td>
                  <td>
                    <div className={hasError ? 'error-field' : ''}>
                      <Select
                        options={headers.map(h => ({ value: h, label: h }))}
                        onChange={(selected) => handleSelect(fieldKey, selected)}
                        value={selectedValue ? { value: selectedValue, label: selectedValue } : null}
                        placeholder="Välj kolumn"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isClearable={!isRequired}
                        isSearchable={true}
                      />

                      {hasError && (
                        <div className="validation-error">
                          <AlertTriangle size={12} />
                          {hasError}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {selectedValue ? (
                      <div className="example-value">
                        {exampleValue !== null && exampleValue !== undefined && exampleValue !== '' ?
                          exampleValue :
                          <span className="example-empty">(tomt värde)</span>
                        }
                      </div>
                    ) : (
                      <span className="example-empty">Ingen kolumn vald</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className={`validation-status ${isValid ? 'validation-status-success' : 'validation-status-error'}`}>
          {isValid ? (
            <>
              <CheckCircle2 className="validation-status-icon" size={20} />
              <span>Alla obligatoriska fält är mappade. Du kan fortsätta till nästa steg.</span>
            </>
          ) : (
            <>
              <AlertTriangle className="validation-status-icon" size={20} />
              <span>Mappa alla obligatoriska fält för att fortsätta till nästa steg.</span>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default GenericImporterColumnMappingStep;